import { makeObservable, observable, action, computed } from 'mobx'
import type { Message, Room } from '@core/models'
import {
  RoomOpen,
  RoomClose,
} from '@core/models'
import axios from 'axios'
import { BaseStore } from './BaseStore'
import type { AuthStore } from './AuthStore'

export interface WSEvent {
  type: 'message' | 'room_created' | 'room_updated'
  room_id?: string
  message?: Message
  room?: Room
}

export class RoomStore extends BaseStore {
  private authStore: AuthStore

  rooms = new Map<string, Room>()
  currentRoomId = ''
  currentMessages: Message[] = []
  loadingMessages = false

  constructor(authStore: AuthStore) {
    super()
    this.authStore = authStore

    makeObservable(this, {
      rooms: observable,
      currentRoomId: observable,
      currentMessages: observable,
      loadingMessages: observable,
      waitingRooms: computed,
      myRooms: computed,
      fetchRooms: action,
      fetchRoomDetail: action,
      assignRoom: action,
      releaseRoom: action,
      closeRoom: action,
      reopenRoom: action,
      applyEvent: action,
      setCurrentRoom: action,
    })
  }

  get waitingRooms(): Room[] {
    return Array.from(this.rooms.values())
      .filter((room) => room.state === RoomOpen && !room.user)
      .sort((a, b) => (b.last_message_at || b.created || '').localeCompare(a.last_message_at || a.created || ''))
  }

  get myRooms(): Room[] {
    const me = this.authStore.user?.id || ''
    return Array.from(this.rooms.values())
      .filter((room) => room.state === RoomOpen && room.user === me)
      .sort((a, b) => (b.last_message_at || b.created || '').localeCompare(a.last_message_at || a.created || ''))
  }

  get currentRoom(): Room | undefined {
    return this.rooms.get(this.currentRoomId)
  }

  apiPath(path: string) {
    const tenantId = this.authStore.currentTenantId
    return `${super.apiPath(`v1/tenants/${tenantId}`)}/${path}`
  }

  async fetchRooms() {
    if (!this.authStore.currentTenantId) return

    await this.handleApiCall(async () => {
      const response = await axios.get<Room[]>(this.apiPath('rooms'), {
        headers: this.generateFetchHeader(),
      })
      for (const room of response.data || []) {
        if (room.id) this.rooms.set(room.id, room)
      }
      // drop rooms that disappeared (e.g. taken by others / closed)
      const visibleIds = new Set((response.data || []).map((r) => r.id))
      for (const id of Array.from(this.rooms.keys())) {
        if (!visibleIds.has(id)) this.rooms.delete(id)
      }
      return response.data
    })
  }

  async fetchRoomDetail(roomId: string) {
    this.loadingMessages = true
    try {
      const response = await axios.get<{ messages: Message[] } & Room>(this.apiPath(`rooms/${roomId}`), {
        headers: this.generateFetchHeader(),
      })
      if (response.data.id) {
        const { messages, ...room } = response.data
        this.rooms.set(roomId, room as Room)
        this.currentMessages = messages || []
      }
    } finally {
      this.loadingMessages = false
    }
  }

  async assignRoom(roomId: string): Promise<boolean> {
    const result = await this.handleApiCall(async () => {
      const response = await axios.post<Room>(
        this.apiPath(`rooms/${roomId}/assign`),
        {},
        { headers: this.generateFetchHeader() }
      )
      return response.data
    })

    if (!result) return false

    this.rooms.set(result.id, result)
    this.tryShowToast('ルームの対応を開始しました', 'success')
    return true
  }

  async releaseRoom(roomId: string): Promise<boolean> {
    const result = await this.handleApiCall(async () => {
      const response = await axios.delete<Room>(this.apiPath(`rooms/${roomId}/assign`), {
        headers: this.generateFetchHeader(),
      })
      return response.data
    })

    if (!result) return false

    this.rooms.set(result.id, result)
    this.tryShowToast('対応を解除しました', 'info')
    return true
  }

  async closeRoom(roomId: string): Promise<boolean> {
    const result = await this.handleApiCall(async () => {
      const response = await axios.post<Room>(this.apiPath(`rooms/${roomId}/close`), {}, {
        headers: this.generateFetchHeader(),
      })
      return response.data
    })

    if (!result) return false

    this.rooms.set(result.id, result)
    this.tryShowToast('ルームをクローズしました', 'success')
    return true
  }

  async reopenRoom(roomId: string): Promise<boolean> {
    const result = await this.handleApiCall(async () => {
      const response = await axios.post<Room>(this.apiPath(`rooms/${roomId}/reopen`), {}, {
        headers: this.generateFetchHeader(),
      })
      return response.data
    })

    if (!result) return false

    this.rooms.set(result.id, result)
    this.tryShowToast('ルームを再開しました', 'success')
    return true
  }

  async requestUploadUrl(roomId: string, filename: string, contentType: string) {
    const result = await this.handleApiCall(async () => {
      const response = await axios.post<{ upload_url: string; file_url: string; key: string }>(
        this.apiPath(`rooms/${roomId}/upload-url`),
        { filename, content_type: contentType },
        { headers: this.generateFetchHeader() }
      )
      return response.data
    })
    return result
  }

  async uploadImage(roomId: string, file: File): Promise<string | null> {
    const presigned = await this.requestUploadUrl(roomId, file.name, file.type)
    if (!presigned) return null

    try {
      await axios.put(presigned.upload_url, file, {
        headers: { 'Content-Type': file.type },
      })
      return presigned.file_url
    } catch (error) {
      console.error('Failed to upload image:', error)
      this.setError('画像のアップロードに失敗しました')
      return null
    }
  }

  applyEvent(event: WSEvent) {
    switch (event.type) {
      case 'room_created':
      case 'room_updated': {
        if (event.room && event.room.id) {
          const existing = this.rooms.get(event.room.id)
          this.rooms.set(event.room.id, { ...existing, ...event.room })

          // remove closed rooms from the map entirely
          if (event.room.state === RoomClose) {
            if (this.currentRoomId === event.room.id) {
              // keep it visible until the operator leaves
            }
          }
          if (existing === undefined && event.type === 'room_updated') {
            // unknown update; refresh list in background
            void this.fetchRooms()
          }
        }
        break
      }
      case 'message': {
        if (event.message && event.room_id) {
          const room = this.rooms.get(event.room_id)
          if (room) {
            room.last_message_at = event.message.created
            room.last_message_preview =
              event.message.type === 'image' ? '[画像]' : event.message.body
          }
          if (this.currentRoomId === event.room_id) {
            this.currentMessages = [...this.currentMessages, event.message]
          }
        }
        break
      }
    }
  }

  setCurrentRoom(roomId: string) {
    this.currentRoomId = roomId
    this.currentMessages = []
  }
}